import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { Crown, Trophy, Loader2, Sparkles, Medal, ArrowRight, Star } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RANK_BADGE = (rank, cutoff) => {
  if (rank === 1) return 'bg-amber-400 text-amber-900';
  if (rank === 2) return 'bg-slate-300 text-slate-700';
  if (rank === 3) return 'bg-orange-400 text-orange-900';
  if (rank <= cutoff) return 'bg-emerald-100 text-emerald-700';
  return 'bg-slate-100 text-slate-500';
};

const ProLeaderboard = () => {
  const { user } = useAuth();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/leaderboard/pro-monthly`);
        setBoard(data);
      } catch (e) { console.error('Failed to load pro leaderboard:', e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const isPro = user?.subscription_status === 'active' || user?.role === 'admin';

  if (loading) {
    return (
      <div className="min-h-screen"><Header />
        <div className="flex items-center justify-center py-32"><Loader2 className="w-10 h-10 text-[#0EA5E9] animate-spin" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="pro-leaderboard-page">
      <Header />
      <main className="max-w-5xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold uppercase tracking-wider mb-4">
            <Crown className="w-4 h-4" /> Pro Monthly Leaderboard
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-[#111827] mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid="pro-leaderboard-title">
            {board?.month_label}
          </h1>
          <p className="text-base text-[#6B7280] max-w-2xl mx-auto">
            Top <strong className="text-amber-600">{board?.top_percent}%</strong> of Pro performers this month win <strong className="text-amber-600">⭐ Super Points</strong> on the 1st of next month.
          </p>
        </div>

        {/* Reward tiers card */}
        <div className="mb-6 rounded-3xl p-5 bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-200" data-testid="reward-tiers-card">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
            <div>
              <h3 className="font-bold text-amber-800 flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                <Star className="w-5 h-5 fill-amber-500 text-amber-500" /> This month's super-point pool
              </h3>
              <p className="text-xs text-amber-700 mt-0.5">
                {board?.tiered_active
                  ? `🔥 Tiered prizes UNLOCKED — ${board?.current_mau?.toLocaleString()}+ MAU this month!`
                  : `${(board?.current_mau || 0).toLocaleString()} / ${(board?.mau_tiered_threshold || 1000).toLocaleString()} MAU — keep growing to unlock big prizes`}
              </p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${board?.tiered_active ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-600 border border-slate-200'}`}>
              {board?.tiered_active ? 'BIG PRIZES' : `Base ${board?.reward_tiers?.rest_of_top_3_percent || 100} ⭐`}
            </span>
          </div>
          {/* MAU progress bar */}
          {!board?.tiered_active && (
            <div className="mb-4" data-testid="mau-progress">
              <div className="h-2 rounded-full bg-amber-100 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all" style={{ width: `${Math.min(100, ((board?.current_mau || 0) / (board?.mau_tiered_threshold || 1000)) * 100)}%` }} />
              </div>
            </div>
          )}
          {/* Tier breakdown */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="p-3 rounded-2xl bg-white border border-amber-200 text-center" data-testid="tier-rank-1">
              <p className="text-[10px] uppercase tracking-wider text-amber-600 font-bold">🥇 Rank 1</p>
              <p className="text-lg sm:text-xl font-black text-amber-700" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                {(board?.reward_tiers?.rank_1 || 100).toLocaleString()}
              </p>
              <p className="text-[10px] text-amber-600">⭐ super pts</p>
            </div>
            <div className="p-3 rounded-2xl bg-white border border-amber-200 text-center" data-testid="tier-rank-2">
              <p className="text-[10px] uppercase tracking-wider text-slate-600 font-bold">🥈 Rank 2</p>
              <p className="text-lg sm:text-xl font-black text-slate-700" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                {(board?.reward_tiers?.rank_2 || 100).toLocaleString()}
              </p>
              <p className="text-[10px] text-slate-600">⭐ super pts</p>
            </div>
            <div className="p-3 rounded-2xl bg-white border border-amber-200 text-center" data-testid="tier-rank-3">
              <p className="text-[10px] uppercase tracking-wider text-orange-700 font-bold">🥉 Rank 3</p>
              <p className="text-lg sm:text-xl font-black text-orange-700" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                {(board?.reward_tiers?.rank_3 || 100).toLocaleString()}
              </p>
              <p className="text-[10px] text-orange-700">⭐ super pts</p>
            </div>
            <div className="p-3 rounded-2xl bg-white border border-amber-200 text-center" data-testid="tier-rest">
              <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Top {board?.top_percent || 3}%</p>
              <p className="text-lg sm:text-xl font-black text-emerald-700" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                {(board?.reward_tiers?.rest_of_top_3_percent || 100).toLocaleString()}
              </p>
              <p className="text-[10px] text-emerald-700">⭐ super pts each</p>
            </div>
          </div>
        </div>

        {!isPro && (
          <div className="mb-6 rounded-2xl p-5 bg-gradient-to-r from-amber-400 to-yellow-500 text-white shadow-lg" data-testid="pro-leaderboard-paywall">
            <div className="flex items-center gap-4">
              <Trophy className="w-10 h-10 shrink-0" />
              <div className="flex-1">
                <h3 className="font-bold mb-0.5" style={{ fontFamily: 'Fredoka, sans-serif' }}>Compete to win monthly rewards</h3>
                <p className="text-sm opacity-95">Only Pro subscribers compete for the top 3% bonus. Upgrade to join the race.</p>
              </div>
              <Link to="/upgrade" className="px-4 py-2 bg-white text-amber-600 rounded-xl font-bold text-sm hover:bg-amber-50 transition flex items-center gap-1">
                Upgrade <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}

        {/* My rank card */}
        {isPro && board?.my_rank && (
          <div className="mb-6 rounded-2xl p-5 bg-gradient-to-r from-[#0EA5E9] to-[#0284C7] text-white shadow-lg" data-testid="my-rank-card">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center font-black text-xl backdrop-blur-sm" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                #{board.my_rank.rank}
              </div>
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wider opacity-80">Your rank this month</p>
                <p className="text-2xl font-black" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  {board.my_rank.total_score} pts · {board.my_rank.attempts} attempts
                </p>
                {board.my_rank.rank <= board.win_cutoff_rank ? (
                  <p className="text-sm mt-1 flex items-center gap-1"><Sparkles className="w-4 h-4 text-amber-300" /> You're in the winning zone!</p>
                ) : (
                  <p className="text-sm mt-1 opacity-90">{board.win_cutoff_rank - board.my_rank.rank + 1} more ranks to crack the top {board.top_percent}%.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Leaderboard */}
        <div className="rounded-3xl bg-white border border-slate-100 shadow-sm overflow-hidden" data-testid="pro-leaderboard-list">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="font-bold text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Pro rankings — live</h2>
              <p className="text-xs text-[#6B7280]">{board?.total_pro_competitors || 0} Pro members competing</p>
            </div>
            <Medal className="w-6 h-6 text-amber-500" />
          </div>
          {(!board?.rankings || board.rankings.length === 0) ? (
            <div className="py-16 text-center text-sm text-[#9CA3AF]" data-testid="pro-leaderboard-empty">
              No Pro attempts this month yet. Be the first to put yourself on the board!
            </div>
          ) : (
            <ul>
              {board.rankings.map((r) => (
                <li
                  key={r.user_id}
                  className={`flex items-center gap-4 px-5 py-3 border-b border-slate-50 last:border-b-0 hover:bg-slate-50 transition ${r.user_id === user?._id ? 'bg-blue-50' : ''}`}
                  data-testid={`pro-rank-${r.rank}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-sm ${RANK_BADGE(r.rank, board.win_cutoff_rank)}`} style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    {r.rank === 1 ? <Crown className="w-4 h-4" /> : `#${r.rank}`}
                  </div>
                  {r.picture ? (
                    <img src={r.picture} alt={r.name} className="w-9 h-9 rounded-full object-cover" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0EA5E9] to-[#0284C7] flex items-center justify-center text-white text-sm font-bold">
                      {(r.name || '?')[0].toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#111827] truncate">{r.name || 'Aspirant'}</p>
                    {r.institute && <p className="text-xs text-[#9CA3AF] truncate">{r.institute}</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-black text-[#0EA5E9]" style={{ fontFamily: 'Fredoka, sans-serif' }}>{r.total_score}</p>
                    <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">{r.attempts} attempts</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Last month winners */}
        {board?.last_month?.winners?.length > 0 && (
          <div className="mt-8 rounded-2xl p-5 bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200" data-testid="last-month-winners">
            <h3 className="font-bold text-amber-700 mb-3 flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              <Trophy className="w-5 h-5" /> Last month's champions
              {board.last_month.tiered && <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] uppercase bg-emerald-100 text-emerald-700">Big-prize tier</span>}
            </h3>
            <ul className="space-y-2">
              {board.last_month.winners.slice(0, 10).map((w) => (
                <li key={w.user_id} className="flex items-center justify-between text-sm">
                  <span className="text-[#111827]">
                    <strong>#{w.rank || '—'} {w.name || 'Aspirant'}</strong>
                    <span className="text-[#9CA3AF] ml-2">· {w.total_score} pts</span>
                  </span>
                  <span className="flex items-center gap-1 text-amber-700 font-bold">
                    <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                    {(w.super_points_awarded || 100).toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-amber-700 opacity-80">
              {board.last_month.winners_count} winners earned super points based on {(board.last_month.mau || 0).toLocaleString()} MAU.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default ProLeaderboard;
